import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { SectionLabel } from "@/pages/Home";
import { SERVICES } from "@/data/services";
import { SERVICE_EXTRA, SERVICE_FAQS } from "@/data/servicesExtra";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ServiceDetail() {
  const { lang, t, ml } = useI18n();
  const { slug } = useParams();
  const L = (p) => `/${lang}${p}`;
  const service = SERVICES.find((s) => s.slug === slug);

  useSEO({
    lang,
    title: service ? `${ml(service.title)} | HO-SYS` : "HO-SYS",
    description: service ? ml(service.short) : "",
    path: `/services/${slug}`,
    image: service?.image,
  });

  if (!service) return <Navigate to={L("/services")} replace />;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);
  const extra = SERVICE_EXTRA[slug] || { problems: [], benefits: [] };

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <Link to={L("/services")} data-testid="service-back"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {t("nav.services")}
        </Link>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="w-14 h-14 rounded-2xl bg-neon/10 border border-neon/25 flex items-center justify-center mb-6">
              <Icon name={service.icon} className="w-7 h-7 text-neon" />
            </div>
            <SectionLabel>{t("servicePage.label")}</SectionLabel>
            <h1 className="mt-4 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl">{ml(service.title)}</h1>
            <p className="mt-5 text-white/60 text-lg leading-relaxed">{ml(service.description)}</p>
          </Reveal>
          <Reveal delay={0.1} className="relative">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={service.image} alt={ml(service.title)} loading="lazy" className="w-full h-[360px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <Reveal className="mb-10"><h2 className="font-display text-3xl font-bold text-white">{t("servicePage.featuresTitle")}</h2></Reveal>
        <div className="grid sm:grid-cols-2 gap-4">
          {service.features.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#0A0A0A] p-5">
                <div className="w-9 h-9 shrink-0 rounded-full bg-neon/15 border border-neon/30 flex items-center justify-center">
                  <Check className="w-4 h-4 text-neon" />
                </div>
                <span className="text-white/85">{ml(f)}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10">
        <div className="grid md:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-white mb-6">{t("servicePage.problemsTitle")}</h2>
            <div className="space-y-4">
              {extra.problems.map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-red-400 shrink-0" />
                  <span className="text-white/70">{ml(p)}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-2xl font-bold text-white mb-6">{t("servicePage.benefitsTitle")}</h2>
            <div className="space-y-4">
              {extra.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="mt-0.5 w-4 h-4 text-lime shrink-0" />
                  <span className="text-white/70">{ml(b)}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10">
        <h2 className="font-display text-2xl font-bold text-white mb-8">{t("servicePage.faqTitle")}</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
          {SERVICE_FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white hover:text-neon hover:no-underline">{ml(f.q)}</AccordionTrigger>
              <AccordionContent className="text-white/60">{ml(f.a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 border-t border-white/10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-2xl font-bold text-white">{t("servicePage.otherServices")}</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {others.map((s) => (
            <Link key={s.slug} to={L(`/services/${s.slug}`)} data-testid={`other-service-${s.slug}`}
              className="group rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-neon/40 transition-colors">
              <Icon name={s.icon} className="w-6 h-6 text-neon mb-4" />
              <h3 className="font-display text-lg font-semibold text-white">{ml(s.title)}</h3>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-neon opacity-0 group-hover:opacity-100 transition-opacity">
                {t("common.explore")} <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
        <Reveal className="mt-14 rounded-3xl border border-white/10 bg-[#0A0A0A] p-10 text-center relative overflow-hidden">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[400px] h-[240px] bg-neon/15 blur-[110px] rounded-full" />
          <div className="relative">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">{t("servicePage.cta")}</h3>
            <Link to={L("/contact")} data-testid="service-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-semibold text-black hover:bg-white transition-colors">
              {t("nav.demo")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
